import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Snackbar from "@mui/material/Snackbar";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { AddToQueueIcon } from "../../shared/AddToQueueIcon";
import iconUnfavorite from "../../../assets/icon-unfavorite.svg";
import {
  useAppStore,
  selectAssessmentSidebarIds,
  selectSelectedRequirementIds,
} from "../../../stores";
import { useGlobalConfig } from "../../../hooks/useGlobalConfig";
import {
  exportRegulationsToXlsx,
  addRegulationFavorites,
  getRegulationById,
  removeRegulationFavorites,
} from "../../../services/regulationsService";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useGuardedAsyncCallback } from "../../../hooks/useGuardedAsyncCallback";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import { RunAiAssessmentMenuButton } from "../RunAiAssessmentMenuButton";
import iconFlash from "../../../assets/icon-flash.svg";
import {
  StartAutoTaggingDialog,
  buildAutoTaggingRegulationsFromRows,
  resolveAutoTaggingRegulationsForIds,
} from "../AutoTagging";
import type { AutoTaggingBundleTaskRegulation } from "../../../services/autoTaggingService";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import type { AutoTaggingRegulationSource } from "../AutoTagging/autoTaggingRegulations";

export type BulkActionRowLookup = (
  id: string,
) => AutoTaggingRegulationSource | null | undefined;

export type BulkActionsProps = Readonly<{
  onCompanyScopeImpact?: () => void;
  onCustomScopeImpact?: () => void;
  companyScopeStarting?: boolean;
  /**
   * Card view supplies the current rows. List view resolves rows from the grid.
   */
  lookupRow?: BulkActionRowLookup;
  /** After successful bulk favorite toggle — update grid row favorite state */
  onBulkFavoritesSuccess?: (
    regulationIds: string[],
    isFavorite?: boolean,
  ) => void;
}>;

function BulkActionsGridLookup({
  lookupRef,
}: {
  lookupRef: React.MutableRefObject<BulkActionRowLookup | null>;
}) {
  const apiRef = useGridApiContext();
  lookupRef.current = (id) => apiRef.current?.getRow(id);
  return null;
}

export const BulkActions: React.FC<BulkActionsProps> = ({
  onCompanyScopeImpact,
  onCustomScopeImpact,
  companyScopeStarting,
  lookupRow,
  onBulkFavoritesSuccess,
}) => {
  const { showMessage } = useSnackbar();
  const selectedIds = useAppStore(selectSelectedRequirementIds);
  const assessmentSidebarIds = useAppStore(selectAssessmentSidebarIds);
  const addAssessmentSidebarId = useAppStore(
    (state) => state.addAssessmentSidebarId,
  );
  const { config } = useGlobalConfig();
  const maxRegulationsPerBatch = config?.max_regulations_per_batch ?? 5;
  const canExportRegulations = useHasPrivilege("gri.regulation.export");
  const canExecuteAutoTagging = useHasPrivilege("gri.auto_tagging.execute");
  /** Show unless both impact execute privileges are missing. */
  const canAddToAssessmentQueue = useHasPrivilege(
    [
      "gri.assessment.impact.execute",
      "gri.assessment.impact.system.execute",
    ],
    "any",
  );
  const hasSelection = selectedIds.length > 0;

  const [bulkActionAnchorEl, setBulkActionAnchorEl] = useState<null | HTMLElement>(null);
  const [limitMessageOpen, setLimitMessageOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMessageOpen, setExportMessageOpen] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [exportSeverity, setExportSeverity] = useState<"success" | "error">("error");
  const [favoriteSubmitting, setFavoriteSubmitting] = useState(false);
  const [unfavoriteSubmitting, setUnfavoriteSubmitting] = useState(false);
  const [autoTaggingOpen, setAutoTaggingOpen] = useState(false);
  const [resolvingAutoTagging, setResolvingAutoTagging] = useState(false);
  const [autoTaggingRegulations, setAutoTaggingRegulations] = useState<
    AutoTaggingBundleTaskRegulation[]
  >([]);
  const gridLookupRef = useRef<BulkActionRowLookup | null>(null);
  const lookupRowRef = useRef(lookupRow);
  lookupRowRef.current = lookupRow;
  const selectedRegulationSnapshotRef = useRef(
    new Map<string, AutoTaggingBundleTaskRegulation>(),
  );

  const snapshotSelectedRows = useCallback(() => {
    const next = new Map(selectedRegulationSnapshotRef.current);
    for (const id of selectedIds) {
      const row =
        lookupRowRef.current?.(id) ?? gridLookupRef.current?.(id);
      if (!row) continue;
      const built = buildAutoTaggingRegulationsFromRows([row])[0];
      if (built) next.set(id, built);
    }
    for (const key of [...next.keys()]) {
      if (!selectedIds.includes(key)) next.delete(key);
    }
    selectedRegulationSnapshotRef.current = next;
  }, [selectedIds]);

  useEffect(() => {
    snapshotSelectedRows();
  }, [snapshotSelectedRows]);

  const handleBulkActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setBulkActionAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setBulkActionAnchorEl(null);
  };

  const handleExportSelected = async () => {
    if (selectedIds.length === 0 || exporting) return;
    setExporting(true);
    try {
      await exportRegulationsToXlsx(selectedIds);
      setExportMessage("Export started. Check your downloads.");
      setExportSeverity("success");
      setExportMessageOpen(true);
      handleClose();
    } catch (err) {
      setExportMessage(
        err instanceof Error ? err.message : "Export failed. Please try again.",
      );
      setExportSeverity("error");
      setExportMessageOpen(true);
    } finally {
      setExporting(false);
    }
  };

  const handleBulkFavorite = async () => {
    if (selectedIds.length === 0 || favoriteSubmitting) return;
    setFavoriteSubmitting(true);
    try {
      const response = await addRegulationFavorites(selectedIds);
      if (response.status !== "success") {
        throw new Error(response.message || "Failed to add favorites");
      }
      onBulkFavoritesSuccess?.(selectedIds, true);
      showMessage(
        selectedIds.length === 1
          ? "Added to favorites"
          : `Added ${selectedIds.length} regulations to favorites`,
        "success",
      );
      handleClose();
    } catch (err) {
      showMessage(
        err instanceof Error ? err.message : "Failed to update favorites",
        "error",
      );
    } finally {
      setFavoriteSubmitting(false);
    }
  };

  const handleBulkUnfavorite = async () => {
    if (selectedIds.length === 0 || unfavoriteSubmitting) return;
    setUnfavoriteSubmitting(true);
    try {
      await removeRegulationFavorites(selectedIds);
      onBulkFavoritesSuccess?.(selectedIds, false);
      showMessage(
        selectedIds.length === 1
          ? "Removed from favorites"
          : `Removed ${selectedIds.length} regulations from favorites`,
        "success",
      );
      handleClose();
    } catch (err) {
      showMessage(
        err instanceof Error ? err.message : "Failed to update favorites",
        "error",
      );
    } finally {
      setUnfavoriteSubmitting(false);
    }
  };

  const handleOpenAutoTagging = useGuardedAsyncCallback(async () => {
    if (selectedIds.length === 0 || resolvingAutoTagging) return;

    setResolvingAutoTagging(true);
    try {
      snapshotSelectedRows();
      const regulations = await resolveAutoTaggingRegulationsForIds(
        selectedIds,
        (id) => {
          const cached = selectedRegulationSnapshotRef.current.get(id);
          if (cached) {
            return {
              id,
              regulation_id: cached.regulation_id,
              version: cached.version,
            };
          }
          return lookupRowRef.current?.(id) ?? gridLookupRef.current?.(id);
        },
        async (id) => {
          const result = await getRegulationById(id);
          if (result.status !== "success" || !result.data) return null;
          return result.data;
        },
      );

      if (regulations.length !== selectedIds.length) {
        showMessage(
          "Failed to load all selected regulations for assessment.",
          "error",
        );
        return;
      }

      setAutoTaggingRegulations(regulations);
      setAutoTaggingOpen(true);
      handleClose();
    } catch (err) {
      showMessage(
        err instanceof Error
          ? err.message
          : "Failed to load selected regulations for assessment.",
        "error",
      );
    } finally {
      setResolvingAutoTagging(false);
    }
  });

  const handleAddSelectedToQueue = () => {
    const existingIds = new Set(assessmentSidebarIds);
    const newIds = selectedIds.filter((id) => !existingIds.has(id));
    const nextTotal = assessmentSidebarIds.length + newIds.length;

    if (nextTotal > maxRegulationsPerBatch) {
      setLimitMessageOpen(true);
      handleClose();
      return;
    }

    newIds.forEach((id) => {
      addAssessmentSidebarId(id, undefined, maxRegulationsPerBatch);
    });
    handleClose();
  };

  if (!hasSelection) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {lookupRow ? null : <BulkActionsGridLookup lookupRef={gridLookupRef} />}
      <Button
        variant="contained"
        size="small"
        startIcon={<MoreVertIcon sx={{ fontSize: "16px !important" }} />}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: "16px !important" }} />}
        onClick={handleBulkActionClick}
        sx={{
          bgcolor: "#f5f5f5 !important",
          color: "#262626 !important",
          boxShadow: "none",
          "&:hover": {
            bgcolor: "#e8e8e8 !important",
            boxShadow: "none",
          },
          textTransform: "none",
          fontWeight: 500,
          px: 1.5,
          height: "32px",
          "& .MuiButton-startIcon, & .MuiButton-endIcon": {
            color: "inherit !important",
          },
        }}
      >
        Bulk Action
      </Button>
      <Menu
        anchorEl={bulkActionAnchorEl}
        open={Boolean(bulkActionAnchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 0.5,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            minWidth: 220,
            "& .MuiMenuItem-root": {
              fontSize: "14px",
              py: 1,
            },
            "& .MuiListItemIcon-root": {
              minWidth: "32px !important",
              color: "#595959",
            },
          },
        }}
      >
        {canAddToAssessmentQueue ? (
          <MenuItem onClick={handleAddSelectedToQueue}>
            <ListItemIcon>
              <AddToQueueIcon sx={{ fontSize: "16px" }} />
            </ListItemIcon>
            Add to Assessment Queue
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => void handleBulkFavorite()}
          disabled={favoriteSubmitting}
        >
          <ListItemIcon>
            <StarBorderIcon sx={{ fontSize: "16px" }} />
          </ListItemIcon>
          {favoriteSubmitting ? "Adding…" : "Favorite"}
        </MenuItem>
        <MenuItem
          onClick={() => void handleBulkUnfavorite()}
          disabled={unfavoriteSubmitting}
        >
          <ListItemIcon>
            <Box
              component="img"
              src={iconUnfavorite}
              alt=""
              sx={{ width: 16, height: 16, display: "block" }}
            />
          </ListItemIcon>
          {unfavoriteSubmitting ? "Removing…" : "Unfavorite"}
        </MenuItem>
        {canExportRegulations ? (
          <MenuItem onClick={handleExportSelected} disabled={exporting}>
            <ListItemIcon>
              <FileDownloadOutlinedIcon sx={{ fontSize: "16px" }} />
            </ListItemIcon>
            {exporting ? "Exporting…" : "Export"}
          </MenuItem>
        ) : null}
      </Menu>

      {canExecuteAutoTagging ? (
        <Button
          variant="contained"
          size="small"
          disabled={resolvingAutoTagging}
          onClick={() => void handleOpenAutoTagging()}
          startIcon={
            <Box
              component="img"
              src={iconFlash}
              alt=""
              sx={{ width: 16, height: 16, display: "block" }}
            />
          }
          sx={{
            bgcolor: "#f5f5f5 !important",
            color: "#262626 !important",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#e8e8e8 !important",
              boxShadow: "none",
            },
            textTransform: "none",
            fontWeight: 500,
            px: 1.5,
            height: "32px",
            "& .MuiButton-startIcon, & .MuiButton-endIcon": {
              color: "inherit !important",
            },
          }}
        >
          Run Assessment
        </Button>
      ) : null}

      <RunAiAssessmentMenuButton
        onCompanyScopeImpact={onCompanyScopeImpact}
        onCustomScopeImpact={onCustomScopeImpact}
        companyScopeStarting={companyScopeStarting}
      />
      {canExecuteAutoTagging ? (
        <StartAutoTaggingDialog
          open={autoTaggingOpen}
          onClose={() => setAutoTaggingOpen(false)}
          regulations={autoTaggingRegulations}
        />
      ) : null}
      <Snackbar
        open={limitMessageOpen}
        autoHideDuration={4000}
        onClose={() => setLimitMessageOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {`You can add up to ${maxRegulationsPerBatch} regulations to the assessment queue.`}
        </Alert>
      </Snackbar>
      <Snackbar
        open={exportMessageOpen}
        autoHideDuration={4000}
        onClose={() => setExportMessageOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={exportSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {exportMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
